import ContinueButton from "@components/ContinueButton";
import Error from "@hudson/ui/components/Error";
import Field from "@hudson/ui/components/FormFields/Field";
import Select from "@hudson/ui/components/FormFields/Select";
import { FormProvider, useForm } from "@hudson/ui/components/ReactHookForm";
import Success from "@hudson/ui/components/Success";
import { getProfiles } from "@lib/auth/jwt";
import userProfileDeletePudCc from "@lib/services/user_profile_delete_pud_cc";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import { useState } from "react";
import ContactSection from "./ContactSection";
import CreditCardSection from "./CreditCardSection";
import LocationSection from "./LocationSection";
import { OnSubmitData, useFormSubmit } from "./useFormSubmit";

interface FormProps {
  method: "add" | "edit";
  location?: be.Pud;
  onHide: () => void;
  onSubmit: (data: OnSubmitData) => void;
  pudId?: string;
}

export default function Form({ method, location, onHide, onSubmit, pudId }: FormProps) {
  const formMethods = useForm({
    defaultValues: {
      location,
      creditCard: location?.creditCard,
      pudName: location?.pudName,
      locationType: location?.locationType,
      email: location?.email,
      telephone: location?.telephone,
      telephone2: location?.telephone2,
      faxNumber: location?.faxNumber,
    },
  });
  const { data, loading } = useContent([
    "beErrPudPersonCcRemove",
    "beProfileLocationTypeLabel",
    "beProfileLocationsNewUpdateLocCancelButton",
    "beProfileLocationsNewLocSubmitButton",
    "beProfileLocationsNewLocTitle",
    "beProfileLocationsUpdateLocSubmitButton",
    "beProfileLocationsUpdateLocTitle",
    "bePudPersonCcRemoveSuccessMsg",
  ]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [success, setSuccess] = useState<boolean>();

  const { data: configData } = useConfig(["profileLocationTypesList { name value }", "pudsAsPeople"]);
  const profileLocationTypesList = configData?.config.profileLocationTypesList;
  const pudsAsPeople = configData?.config.pudsAsPeople;

  const { submit, submitting } = useFormSubmit(onSubmit);

  async function deleteCard(ccId: number) {
    const userId = getProfiles().user;
    if (!userId) return;

    setErrorMessage("");
    setSuccess(false);

    const success = await userProfileDeletePudCc(parseInt(location?.id || ""), ccId);

    if (!success) {
      setErrorMessage(data?.content.beErrPudPersonCcRemove);
    } else {
      formMethods.setValue("creditCard", {});
      formMethods.setValue("creditCard.name", "");
      formMethods.setValue("creditCard.number", "");
      formMethods.setValue("creditCard.expiration", "");
      formMethods.setValue("creditCard.address", "");
      formMethods.setValue("creditCard.postalCode", "");
      formMethods.setValue("creditCard.token", "");
      formMethods.setValue("creditCard.numberLast4", "");
      setSuccess(true);
    }
  }

  return (
    <FormProvider {...formMethods}>
      <form
        id={`profile-${method}-location`}
        onSubmit={(e) => {
          e.preventDefault();
          formMethods.handleSubmit(async (data) => await submit(data as OnSubmitData, pudId))();
        }}
        noValidate
      >
        <h3 className="mb-8 text-xl font-bold leading-6 text-gray-900 ">
          {method == "add"
            ? data?.content.beProfileLocationsNewLocTitle
            : data?.content.beProfileLocationsUpdateLocTitle}
        </h3>

        <LocationSection method={method} submitting={submitting} />

        {profileLocationTypesList && profileLocationTypesList.length > 0 && (
          <div className="mt-8 max-w-80">
            <Field
              fieldName="locationType"
              label={data?.content.beProfileLocationTypeLabel}
              fieldControl={(value, onChange, inError, onBlur) => (
                <Select
                  className="w-full"
                  inError={inError}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={String(value || "")}
                  values={Object.fromEntries([
                    ["—", ""],
                    ...profileLocationTypesList.map((opt: { name: string; value: string }) => [opt.name, opt.value]),
                  ])}
                />
              )}
            />
          </div>
        )}

        {pudsAsPeople && <ContactSection />}
        {pudsAsPeople && <CreditCardSection onDelete={deleteCard} />}

        {errorMessage && <Error className="my-6" message={errorMessage} heavyStyle scrollTo />}
        {success && (
          <Success
            className="my-6"
            message={data?.content.bePudPersonCcRemoveSuccessMsg}
            heavyStyle
            scrollTo
          />
        )}

        <div className="flex mt-8 space-x-4">
          <button type="button" className="mr-4 text-gray-500 hover:text-gray-700" onClick={onHide}>
            {data?.content.beProfileLocationsNewUpdateLocCancelButton}
          </button>

          <ContinueButton
            alignLeft
            className="justify-self-start"
            disabled={submitting}
            label={
              method === "add"
                ? data?.content.beProfileLocationsNewLocSubmitButton
                : data?.content.beProfileLocationsUpdateLocSubmitButton
            }
            loading={submitting}
          />
        </div>
      </form>
    </FormProvider>
  );
}
